import type { DerivedConfidence, DerivedStats, Track, TrackPoints } from '@/lib/db';
import { haversineNm } from '@/lib/parse';
import { isNight } from '@/lib/solar';

import { detectGroundEvents } from './ground-events';

/**
 * Turning a track's own fixes into the numbers a draft logbook entry starts
 * from — flight time, distance, top speed and altitude, a takeoff/landing
 * count, and the day/night split of all four.
 *
 * Every number here is a guess a pilot reviews, never a fact `PLAN.md` §2 lets
 * the app assert on its own — which is exactly why `confidence` exists and why
 * the caller (`draft.ts`) only ever writes these into a `derivedFields`-tagged
 * draft, not a confirmed entry.
 *
 * Day/night comes from `lib/solar`'s civil-twilight equations run against
 * each fix's own position and time — no network call, per `ARCHITECTURE.md`
 * §6 — rather than a single sunrise/sunset lookup for the flight as a whole,
 * so a flight that crosses twilight mid-air still splits correctly.
 */

function finiteValues(values: Float32Array | Float64Array): number[] {
  return Array.from(values).filter((value) => Number.isFinite(value));
}

function maxOf(values: readonly number[]): number | undefined {
  return values.length === 0 ? undefined : Math.max(...values);
}

/**
 * Sums the track's own minutes spent under civil twilight, segment by
 * segment — each fix pair's midpoint (time and position) decides whether that
 * stretch was day or night, so a flight that lands after sunset still counts
 * only the dark portion as night.
 */
function nightMinutes(points: TrackPoints): number | undefined {
  const { t, lat, lon } = points;
  let minutes = 0;
  let counted = false;
  for (let i = 1; i < t.length; i += 1) {
    const t0 = t[i - 1];
    const t1 = t[i];
    const lat0 = lat[i - 1];
    const lon0 = lon[i - 1];
    const lat1 = lat[i];
    const lon1 = lon[i];
    if (
      t0 === undefined ||
      t1 === undefined ||
      lat0 === undefined ||
      lon0 === undefined ||
      lat1 === undefined ||
      lon1 === undefined ||
      !Number.isFinite(t0) ||
      !Number.isFinite(t1) ||
      !Number.isFinite(lat0) ||
      !Number.isFinite(lon0) ||
      !Number.isFinite(lat1) ||
      !Number.isFinite(lon1)
    ) {
      continue;
    }
    counted = true;
    if (isNight(new Date((t0 + t1) / 2), (lat0 + lat1) / 2, (lon0 + lon1) / 2)) {
      minutes += (t1 - t0) / 60_000;
    }
  }
  return counted ? minutes : undefined;
}

function totalDistanceNm(points: TrackPoints): number | undefined {
  const { lat, lon } = points;
  let total = 0;
  let counted = 0;
  for (let i = 1; i < lat.length; i += 1) {
    const lat1 = lat[i - 1];
    const lon1 = lon[i - 1];
    const lat2 = lat[i];
    const lon2 = lon[i];
    if (
      lat1 === undefined ||
      lon1 === undefined ||
      lat2 === undefined ||
      lon2 === undefined ||
      !Number.isFinite(lat1) ||
      !Number.isFinite(lon1) ||
      !Number.isFinite(lat2) ||
      !Number.isFinite(lon2)
    ) {
      continue;
    }
    total += haversineNm(lat1, lon1, lat2, lon2);
    counted += 1;
  }
  return counted === 0 ? undefined : total;
}

export function computeDerivedStats(track: Track, points: TrackPoints): DerivedStats {
  const timeless = track.warnings.some((warning) => warning.kind === 'no-timestamps');
  const airTimeMin = timeless
    ? undefined
    : (Date.parse(track.endTime) - Date.parse(track.startTime)) / 60_000;

  const maxAltFt = maxOf(finiteValues(points.altFt));
  const maxGsKt = maxOf(finiteValues(points.gsKt));
  const distanceNm = totalDistanceNm(points);
  const ground = detectGroundEvents(points);
  const nightMin = timeless ? undefined : nightMinutes(points);

  /*
   * `'low'` is the grid's cue to flag the takeoff/landing cells for review —
   * see `review.ts`. Two things earn it, both about the detector not
   * actually having seen what it is reporting: a recording that broke
   * mid-flight (it deliberately refuses to guess across a hole, so its
   * counts are a floor, not a total) and a count that came from
   * `edgeEventAerodrome` — the one rule in `ground-events.ts` that reads a
   * takeoff or landing off a nearby aerodrome and a distance trend rather
   * than off a ground contact the fixes actually show — and its sibling
   * `runwayGapAt`, which reads a touch-and-go off a gap in coverage over the
   * runway. Either way, the pilot is the one who knows what really happened.
   */
  const confidence: DerivedConfidence =
    timeless ||
    ground === undefined ||
    ground.breaksSkipped ||
    ground.edgeInferred ||
    ground.gapInferred
      ? 'low'
      : 'partial';

  return {
    ...(airTimeMin === undefined ? {} : { airTimeMin: Math.round(airTimeMin) }),
    ...(distanceNm === undefined ? {} : { distanceNm: Math.round(distanceNm * 10) / 10 }),
    ...(maxAltFt === undefined ? {} : { maxAltFt: Math.round(maxAltFt) }),
    ...(maxGsKt === undefined ? {} : { maxGsKt: Math.round(maxGsKt) }),
    ...(ground === undefined
      ? {}
      : {
          landingCount: ground.landingCount,
          touchAndGoCount: ground.touchAndGoCount,
          nightLandingCount: ground.nightLandingCount,
          takeoffCount: ground.takeoffCount,
          nightTakeoffCount: ground.nightTakeoffCount,
        }),
    ...(nightMin === undefined ? {} : { nightMin: Math.round(nightMin) }),
    confidence,
  };
}
